// apps/web/src/pages/billing/BillingWeb.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '@mysimpledb/shared/config/axios';
import './Account.css';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function createRequestId() {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function normalizeDate(value) {
  if (!value) return null;

  if (typeof value === 'number') {
    return new Date(value < 10_000_000_000 ? value * 1000 : value);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = normalizeDate(value);
  return date ? date.toLocaleDateString() : '—';
}

export default function BillingWeb() {
  const location = useLocation();
  const navigate = useNavigate();

  const [billing, setBilling] = useState(null);
  const [sms, setSms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [smsCheckoutBusy, setSmsCheckoutBusy] = useState(false);
  const [smsPackQuantity, setSmsPackQuantity] = useState(1);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [stripeData, setStripeData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const subscription = billing?.subscription || null;
  const subscribed = Boolean(billing?.subscribed);
  const entitled = Boolean(billing?.entitled);

  const statusLabel = useMemo(() => {
    if (!subscription?.status) return 'Not subscribed';
    return String(subscription.status).replace(/_/g, ' ');
  }, [subscription?.status]);

  const loadSummary = async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);

    try {
      setErrorMessage('');
      const { data } = await api.get('/billing/notes/summary');

      setBilling(data?.billing || null);
      setSms(data?.sms || null);
    } catch (error) {
      console.error(
        'Failed to load Notes billing summary:',
        error?.response?.data || error?.message || error
      );

      setErrorMessage(
        error?.response?.data?.error ||
          'Unable to load your subscription information.'
      );
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  useEffect(() => {
    const state = location.state;

    if (state?.fromStripe && state?.stripeData) {
      setStripeData(state.stripeData);
      setShowSuccessModal(true);
      loadSummary({ showSpinner: false });
    } else if (state?.fromStripe === false && state?.error) {
      setErrorMessage(state.error);
    }
  }, [location.state]);

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
    navigate('.', { replace: true, state: {} });
  };

  const handleSubscribe = async () => {
    if (checkoutBusy) return;

    setCheckoutBusy(true);
    setErrorMessage('');

    try {
      const requestId = createRequestId();

      if (!UUID_REGEX.test(requestId)) {
        throw new Error('Could not create a valid billing request ID.');
      }

      const { data } = await api.post(
        '/billing/notes/checkout',
        { requestId },
        {
          headers: {
            'Idempotency-Key': requestId,
          },
        }
      );

      if (!data?.url) {
        throw new Error('Stripe Checkout URL was not returned.');
      }

      window.location.assign(data.url);
    } catch (error) {
      console.error(
        'Failed to start Notes checkout:',
        error?.response?.data || error?.message || error
      );

      const code = error?.response?.data?.error;

      if (code === 'subscription_exists') {
        setErrorMessage(
          'A MySimpleDB Notes subscription already exists for this account.'
        );
        await loadSummary({ showSpinner: false });
      } else if (code === 'notes_account_required') {
        setErrorMessage(
          'This subscription is only available to MySimpleDB Notes accounts.'
        );
      } else {
        setErrorMessage(
          error?.response?.data?.message ||
            error?.message ||
            'Unable to start Stripe Checkout.'
        );
      }
    } finally {
      setCheckoutBusy(false);
    }
  };

  const handleBuySmsCredits = async () => {
    if (smsCheckoutBusy) return;

    const packQuantity = Number(smsPackQuantity);

    if (!Number.isInteger(packQuantity) || packQuantity < 1 || packQuantity > 100) {
      setErrorMessage('SMS pack quantity must be between 1 and 100.');
      return;
    }

    setSmsCheckoutBusy(true);
    setErrorMessage('');

    try {
      const requestId = createRequestId();

      if (!UUID_REGEX.test(requestId)) {
        throw new Error('Could not create a valid billing request ID.');
      }

      const { data } = await api.post(
        '/billing/sms/checkout',
        {
          requestId,
          packQuantity,
        },
        {
          headers: {
            'Idempotency-Key': requestId,
          },
        }
      );

      if (!data?.url) {
        throw new Error('Stripe Checkout URL was not returned.');
      }

      window.location.assign(data.url);
    } catch (error) {
      console.error(
        'Failed to start SMS credits checkout:',
        error?.response?.data || error?.message || error
      );

      const code = error?.response?.data?.error;

      if (code === 'notes_account_required') {
        setErrorMessage(
          'SMS reminder credits are only available to accounts with Notes access.'
        );
      } else if (code === 'sms_purchases_unavailable') {
        setErrorMessage(
          'SMS credit purchases are temporarily unavailable. Please try again later.'
        );
      } else {
        setErrorMessage(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Unable to start SMS credits checkout.'
        );
      }
    } finally {
      setSmsCheckoutBusy(false);
    }
  };

  const handleManageSubscription = async () => {
    if (portalBusy) return;

    setPortalBusy(true);
    setErrorMessage('');

    try {
      const { data } = await api.post('/billing/portal');

      if (!data?.url) {
        throw new Error('Stripe billing portal URL was not returned.');
      }

      window.location.assign(data.url);
    } catch (error) {
      console.error(
        'Failed to open billing portal:',
        error?.response?.data || error?.message || error
      );

      setErrorMessage(
        error?.response?.data?.error === 'no_stripe_customer'
          ? 'No Stripe billing account has been created yet.'
          : 'Unable to open subscription management.'
      );
    } finally {
      setPortalBusy(false);
    }
  };

  return (
    <div>
      <h2>Subscription</h2>

      {errorMessage && (
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 flex items-start justify-between gap-3">
            <div>
              <strong className="block mb-1">Billing message</strong>
              <span>{errorMessage}</span>
            </div>

            <button
              onClick={() => setErrorMessage('')}
              className="text-xs font-medium underline hover:no-underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="section">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <header className="mb-8">
            <p className="text-sm text-gray-500 mt-1">
              MySimpleDB Notes keeps your notes, reminders, categories, and
              synced note data available across your supported devices.
            </p>
          </header>

          <section className="rounded-2xl border bg-white shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold">MySimpleDB Notes</h2>

                  <p className="text-gray-600 mt-1">
                    Your notes and reminders, synchronized across your
                    MySimpleDB Notes apps.
                  </p>
                </div>

                <div className="text-right">
                  <div className="text-3xl font-bold leading-none">$2.99</div>
                  <div className="text-sm text-gray-500">per month</div>
                </div>
              </div>

              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {[
                  'Notes synchronized across supported devices',
                  'Categories and custom note views',
                  'Reminders and due dates',
                  'Rich text formatting for note titles and details',
                  'Desktop, web, and mobile access',
                  'Optional prepaid SMS reminder credits',
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <span
                      aria-hidden
                      className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border"
                    >
                      ✓
                    </span>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                {loading ? (
                  <p className="text-sm text-gray-500">
                    Loading subscription status…
                  </p>
                ) : subscribed ? (
                  <div className="flex flex-col gap-3">
                    <div className="rounded-lg border bg-gray-50 p-4 text-sm">
                      <p style={infoLineStyle}>
                        <strong>Status:</strong> {statusLabel}
                      </p>

                      <p style={infoLineStyle}>
                        <strong>Subscription access:</strong>{' '}
                        {entitled ? 'Active' : 'Not currently active'}
                      </p>

                      <p style={infoLineStyle}>
                        <strong>Next billing date:</strong>{' '}
                        {formatDate(subscription?.currentPeriodEnd)}
                      </p>

                      {subscription?.cancelAtPeriodEnd && (
                        <p style={infoLineStyle}>
                          <strong>Cancellation:</strong> Scheduled for the end
                          of the current billing period
                        </p>
                      )}

                      {sms && (
                        <p style={infoLineStyle}>
                          <strong>SMS credits remaining:</strong>{' '}
                          {Number(sms.creditsRemaining || 0)}
                        </p>
                      )}
                    </div>

                    <div>
                      <button
                        type="button"
                        onClick={handleManageSubscription}
                        disabled={portalBusy}
                        className="px-5 py-2.5 rounded-lg bg-black text-white hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {portalBusy
                          ? 'Opening Stripe…'
                          : 'Manage Subscription'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <button
                      type="button"
                      onClick={handleSubscribe}
                      disabled={checkoutBusy}
                      className="px-5 py-2.5 rounded-lg bg-black text-white hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {checkoutBusy
                        ? 'Opening Stripe…'
                        : 'Subscribe for $2.99/month'}
                    </button>

                    <p className="text-xs text-gray-500 mt-2">
                      Checkout and subscription management are securely handled
                      by Stripe. You can cancel anytime.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 border-t px-6 sm:px-8 py-6">
              <h3 className="font-medium mb-3">How billing works</h3>

              <ol className="list-decimal ml-5 space-y-2 text-sm text-gray-700">
                <li>
                  Click <em>Subscribe for $2.99/month</em> and complete secure
                  checkout through Stripe.
                </li>
                <li>
                  Stripe sends the completed payment to MySimpleDB through a
                  signed webhook.
                </li>
                <li>
                  Your Notes subscription becomes active after the webhook has
                  been processed.
                </li>
                <li>
                  Manage or cancel your subscription through the Stripe billing
                  portal from this page.
                </li>
              </ol>
            </div>
          </section>

          <section className="mt-8 rounded-2xl border bg-white shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold">
                    SMS Reminder Credits
                  </h2>
                  <p className="text-gray-600 mt-1">
                    SMS reminders use prepaid credits. Each $1.00 pack adds 20
                    SMS credits to your account.
                  </p>
                </div>

                <div className="sm:text-right">
                  <div className="text-sm text-gray-500">Current balance</div>
                  <div className="text-3xl font-bold leading-none mt-1">
                    {loading ? '—' : Number(sms?.creditsRemaining || 0)}
                  </div>
                  <div className="text-sm text-gray-500">SMS credits</div>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row sm:items-end gap-4">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-medium">Pack quantity</span>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    value={smsPackQuantity}
                    onChange={(event) => {
                      const next = event.target.value;
                      setSmsPackQuantity(next === '' ? '' : Number(next));
                    }}
                    className="w-28 rounded-lg border px-3 py-2"
                    aria-label="SMS credit pack quantity"
                  />
                </label>

                <div className="text-sm text-gray-600">
                  <div>
                    {Number(smsPackQuantity) > 0
                      ? Number(smsPackQuantity) * 20
                      : 0}{' '}
                    credits
                  </div>
                  <div>
                    Total: $
                    {Number(smsPackQuantity) > 0
                      ? Number(smsPackQuantity).toFixed(2)
                      : '0.00'}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleBuySmsCredits}
                  disabled={
                    smsCheckoutBusy ||
                    !Number.isInteger(Number(smsPackQuantity)) ||
                    Number(smsPackQuantity) < 1 ||
                    Number(smsPackQuantity) > 100
                  }
                  className="px-5 py-2.5 rounded-lg bg-black text-white hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {smsCheckoutBusy ? 'Opening Stripe…' : 'Buy SMS Credits'}
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                1 pack = 20 SMS credits = $1.00. Credits remain in your account
                until used. Stripe securely handles the one-time payment.
              </p>
            </div>
          </section>

          <section className="mt-8 grid gap-3">
            <details className="rounded-xl border bg-white p-4">
              <summary className="cursor-pointer font-medium">
                Are SMS reminders included in the subscription?
              </summary>
              <p className="mt-2 text-sm text-gray-700">
                SMS reminders use prepaid SMS credits purchased separately.
                MySimpleDB Notes does not automatically charge for SMS usage.
              </p>
            </details>

            <details className="rounded-xl border bg-white p-4">
              <summary className="cursor-pointer font-medium">
                Is my payment information secure?
              </summary>
              <p className="mt-2 text-sm text-gray-700">
                Checkout and subscription management are handled by Stripe.
                MySimpleDB does not store your payment card information.
              </p>
            </details>

            <details className="rounded-xl border bg-white p-4">
              <summary className="cursor-pointer font-medium">
                Can I cancel later?
              </summary>
              <p className="mt-2 text-sm text-gray-700">
                Yes. Use Manage Subscription to open the Stripe billing portal
                and manage or cancel your subscription.
              </p>
            </details>
          </section>
        </div>
      </div>

      {showSuccessModal && stripeData && (
        <div style={backdropStyle}>
          <div style={modalStyle}>
            <h2 style={{ marginTop: 0, marginBottom: 10 }}>
              Subscription Updated
            </h2>

            <p style={{ fontSize: 14, marginBottom: 14 }}>
              Your MySimpleDB Notes subscription has been processed.
            </p>

            <div style={infoBoxStyle}>
              <p style={infoLineStyle}>
                <strong>Plan:</strong> MySimpleDB Notes
              </p>

              <p style={infoLineStyle}>
                <strong>Status:</strong>{' '}
                {stripeData.status || subscription?.status || 'processing'}
              </p>

              <p style={infoLineStyle}>
                <strong>Next billing date:</strong>{' '}
                {formatDate(
                  stripeData.currentPeriodEnd ||
                    subscription?.currentPeriodEnd
                )}
              </p>
            </div>

            <button onClick={handleCloseSuccess} style={buttonStyle}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const backdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,23,42,0.65)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
};

const modalStyle = {
  background: '#0b1120',
  color: '#f9fafb',
  padding: '20px 24px',
  borderRadius: 12,
  maxWidth: 420,
  width: '90%',
  boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
  border: '1px solid #374151',
};

const infoBoxStyle = {
  background: '#020617',
  borderRadius: 10,
  padding: '10px 12px',
  marginBottom: 16,
  fontSize: 13,
};

const infoLineStyle = {
  margin: '4px 0',
};

const buttonStyle = {
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  background: '#2563eb',
  color: '#f9fafb',
};
